import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { serviceTypesApi } from '@/api/service-types'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { showError } from '@/lib/error'
import type { ServiceType } from '@/types/service-record'
import { ServiceTypeDialog } from './components/ServiceTypeDialog'
import { ServiceTypeTable } from './components/ServiceTypeTable'

export function ServiceTypesPage() {
  const queryClient = useQueryClient()
  const [keyword, setKeyword] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [editing, setEditing] = useState<ServiceType | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['service-types', 'list', { keyword: searchTerm, page: 1, page_size: 200 }],
    queryFn: () => serviceTypesApi.list({ keyword: searchTerm, page: 1, page_size: 200 }),
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['service-types'] })
  }

  const handleDelete = async (t: ServiceType) => {
    if (!window.confirm(`确定删除服务类型「${t.label}」？`)) return
    try {
      await serviceTypesApi.delete(t.id)
      toast.success('服务类型已删除')
      refresh()
    } catch (error) {
      showError(error, '删除失败')
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">服务类型</h1>
          <p className="text-sm text-muted-foreground">维护客户服务的类型字典与排序</p>
        </div>
        <PermissionGuard permission="service_type:create">
          <Button onClick={() => { setEditing(null); setDialogOpen(true) }}>
            <Plus className="mr-2 h-4 w-4" />新增类型
          </Button>
        </PermissionGuard>
      </div>
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex gap-2 max-w-sm">
            <Input
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && setSearchTerm(keyword)}
              placeholder="搜索类型名称"
            />
            <Button variant="secondary" onClick={() => setSearchTerm(keyword)}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">加载中…</p>
          ) : (
            <ServiceTypeTable
              types={data?.list ?? []}
              onEdit={t => { setEditing(t); setDialogOpen(true) }}
              onDelete={handleDelete}
              onChanged={refresh}
              sortingDisabled={searchTerm.trim().length > 0}
            />
          )}
        </CardContent>
      </Card>
      <ServiceTypeDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={refresh} />
    </div>
  )
}
